import { useEffect, useMemo, useState } from 'react';

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        ...options,
    });

    const bodyText = await response.text();
    if (!response.ok) {
        let parsed = null;
        try {
            parsed = JSON.parse(bodyText);
        } catch (e) {
            // ignore parse error
        }
        const message = parsed?.message || `${response.status} ${response.statusText}: ${bodyText}`;
        const error = new Error(message);
        error.status = response.status;
        error.body = bodyText;
        throw error;
    }

    return bodyText ? JSON.parse(bodyText) : null;
};

const getInventoryIdFromPath = (path = '') => {
    const normalizedPath = (path || '').toLowerCase().replace(/\/$/, '');
    if (!normalizedPath.startsWith('/ingredient-inventories')) return null;
    const match = normalizedPath.match(/^\/ingredient-inventories\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : null;
};

const formatDifference = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
    const numericValue = Number(value);
    const rounded = Math.abs(numericValue).toFixed(3).replace(/\.?0+$/, '');
    return `${numericValue > 0 ? '+' : ''}${rounded}`;
};

export default function IngredientInventoriesTab({ onInventoryChanged } = {}) {
    const [inventories, setInventories] = useState([]);
    const [inventory, setInventory] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [savingItemId, setSavingItemId] = useState(null);
    const [validating, setValidating] = useState(false);
    const [error, setError] = useState(null);
    const [createFormOpen, setCreateFormOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ service: null, inventory_type: 'main' });
    const [searchQuery, setSearchQuery] = useState('');

    const inventoryId = useMemo(() => getInventoryIdFromPath(window.location.pathname), []);
    const isDetailMode = Boolean(inventoryId);

    useEffect(() => {
        const loadInventories = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchJson('/api/inventory/ingredient-inventories');
                setInventories(Array.isArray(data) ? data : []);
                setInventory(null);
            } catch (err) {
                console.error('Impossible de charger les inventaires :', err);
                setError('Impossible de charger les inventaires.');
            } finally {
                setLoading(false);
            }
        };

        const loadInventoryDetail = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchJson(`/api/inventory/ingredient-inventories/${inventoryId}`);
                setInventory(data);
                setInventories([]);
                setDrafts({});
            } catch (err) {
                console.error('Impossible de charger l’inventaire :', err);
                setError('Impossible de charger l’inventaire demandé.');
            } finally {
                setLoading(false);
            }
        };

        if (isDetailMode) {
            loadInventoryDetail();
            return;
        }

        loadInventories();
    }, [inventoryId, isDetailMode]);

    const createInventory = async () => {
        setCreating(true);
        setError(null);

        try {
            const response = await fetchJson('/api/inventory/ingredient-inventories', {
                method: 'POST',
                body: JSON.stringify({
                    inventory_date: new Date().toISOString().slice(0, 10),
                    service: createForm.service,
                    inventory_type: createForm.inventory_type,
                }),
            });

            setInventories((prev) => [response, ...prev]);
            setCreateFormOpen(false);
            setCreateForm({ service: null, inventory_type: 'main' });
            window.location.href = `/ingredient-inventories/${response.id}`;
        } catch (err) {
            console.error('Erreur lors de la création de l’inventaire :', err);
            setError('Impossible de créer l’inventaire. Veuillez réessayer.');
        } finally {
            setCreating(false);
        }
    };

    const updateItem = async (event, item) => {
        event.preventDefault();
        setSavingItemId(item.id);
        setError(null);

        const draft = drafts[item.id] || {};
        const payload = {
            quantity_real: draft.quantity_real !== undefined ? Number(draft.quantity_real) : item.quantity_real,
            notes: draft.notes !== undefined ? draft.notes : item.notes || '',
        };

        try {
            const response = await fetchJson(`/api/inventory/ingredient-inventories/${inventory.id}/items/${item.ingredient_id}`, {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            setInventory((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    items: prev.items.map((entry) => (entry.id === item.id ? response : entry)),
                };
            });
        } catch (err) {
            console.error('Erreur lors de l’enregistrement de la ligne :', err);
            setError('Impossible d’enregistrer cette ligne pour l’instant.');
        } finally {
            setSavingItemId(null);
        }
    };

    const updateDraftValue = (itemId, field, value) => {
        setDrafts((prev) => ({
            ...prev,
            [itemId]: {
                ...(prev[itemId] || {}),
                [field]: value,
            },
        }));
    };

    const filteredItems = useMemo(() => {
        if (!inventory?.items || !searchQuery.trim()) {
            return inventory?.items || [];
        }
        const query = searchQuery.trim().toLowerCase();
        return inventory.items.filter((item) => {
            const name = item.ingredient?.name?.toLowerCase() || '';
            const notes = item.notes?.toLowerCase() || '';
            const draft = drafts[item.id] || {};
            const draftNotes = draft.notes?.toLowerCase() || '';
            return name.includes(query) || notes.includes(query) || draftNotes.includes(query);
        });
    }, [inventory?.items, searchQuery, drafts]);

    const validateInventory = async () => {
        if (!inventory || validating) return;
        setValidating(true);
        setError(null);

        try {
            const response = await fetchJson(`/api/inventory/ingredient-inventories/${inventory.id}/validate`, {
                method: 'POST',
            });
            setInventory((prev) => prev ? ({ ...prev, ...response.inventory }) : prev);
            if (typeof onInventoryChanged === 'function') {
                await onInventoryChanged();
            }
        } catch (err) {
            console.error('Erreur lors de la validation de l’inventaire :', err);
            setError('Impossible de valider cet inventaire pour l’instant.');
        } finally {
            setValidating(false);
        }
    };

    if (isDetailMode) {
        return (
            <div className="h-screen overflow-y-auto px-6 py-6">
                <section className="mb-6 rounded-3xl border border-emerald-400 bg-emerald-50/90 p-6 shadow-xl shadow-slate-950/10">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                            <p className="text-sm uppercase tracking-[0.35em] text-emerald-500">Inventaire détaillé</p>
                            <h2 className="text-3xl font-semibold text-emerald-900">
                                Inventaire du {inventory ? formatDate(inventory.inventory_date) : '…'}
                                {inventory?.service && (
                                    <span className="ml-2 inline-block rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-700 capitalize">
                                        {inventory.service}
                                    </span>
                                )}
                                {inventory && (
                                    <span className={`ml-2 inline-block rounded-full px-3 py-1 text-sm capitalize ${inventory.inventory_type === 'work' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                                        {inventory.inventory_type === 'work' ? 'Travail' : 'Principal'}
                                    </span>
                                )}
                            </h2>
                            <p className="mt-2 text-sm text-emerald-700">
                                Saisissez la quantité réelle observée pour chaque ingrédient puis enregistrez la ligne.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
                                <label htmlFor="inventory-search" className="sr-only">Rechercher un ingrédient</label>
                                <input
                                    id="inventory-search"
                                    type="search"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Rechercher un ingrédient..."
                                    className="w-full border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                                />
                            </div>
                            {inventory && (
                                <a
                                    href={`/ingredient-inventories/${inventory.id}/print`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center rounded-3xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                                >
                                    🖨️ Imprimer la liste
                                </a>
                            )}
                        </div>
                    </div>
                    {inventory?.is_validated ? (
                        <p className="mt-2 text-sm font-semibold text-emerald-800">
                            Validé par {inventory.validated_by?.name || 'un utilisateur'} le {inventory.validated_at ? new Date(inventory.validated_at).toLocaleString('fr-FR') : '—'}.
                        </p>
                    ) : (
                        <p className="mt-2 text-sm text-emerald-700">
                            La validation applique les écarts au stock et crée un mouvement d’historique pour chaque article concerné.
                        </p>
                    )}
                    <div className="flex flex-wrap gap-3">
                            <a
                                href={`/ingredient-inventories`}
                                className="inline-flex items-center justify-center rounded-3xl border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
                            >
                                ← Retour
                            </a>
                            {inventory && (
                                <>
                                    <button
                                        type="button"
                                        onClick={validateInventory}
                                        disabled={validating || inventory.is_validated}
                                        className="inline-flex items-center justify-center rounded-3xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                                    >
                                        {inventory.is_validated ? '✅ Validé' : validating ? 'Validation…' : 'Valider l’inventaire'}
                                    </button>
                                    <a
                                        href={`/ingredient-inventories/${inventory.id}/print`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center justify-center rounded-3xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                                    >
                                        🖨️ Imprimer la liste
                                    </a>
                                </>
                            )}
                        </div>
                </section>

                {error && (
                    <div className="mb-6 rounded-3xl border border-rose-300 bg-rose-50 p-4 text-rose-700">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="rounded-3xl border border-slate-200 bg-white p-6 text-slate-500 shadow-sm">
                        Chargement de l’inventaire…
                    </div>
                ) : inventory?.items?.length ? (
                    <div className="space-y-3">
                        {filteredItems.map((item) => {
                            const draft = drafts[item.id] || {};
                            const realValue = draft.quantity_real !== undefined ? draft.quantity_real : item.quantity_real;
                            const hasReal = realValue !== null && realValue !== undefined && realValue !== '';
                            const diff = hasReal ? Number(realValue) - Number(item.quantity_expected) : null;
                            const diffClass = diff === null ? '' : (diff === 0 ? 'text-emerald-700 bg-emerald-100' : (diff < 0 ? 'text-rose-700 bg-rose-100' : 'text-amber-700 bg-amber-100'));

                            return (
                                <form
                                    key={item.id}
                                    onSubmit={(event) => updateItem(event, item)}
                                    className={`rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition ${hasReal ? 'border-emerald-300 bg-emerald-50/70' : ''}`}
                                >
                                    <div className="grid gap-4 lg:grid-cols-[2fr_1.2fr_1fr_auto] lg:items-end">
                                        <div className="space-y-1">
                                            <p className="text-lg font-semibold text-slate-900">{item.ingredient?.name || 'Ingrédient'}</p>
                                            <p className="text-sm text-slate-600">
                                                Attendu : {Number(item.quantity_expected).toFixed(3)} {item.ingredient?.unit?.abbreviation || ''}
                                            </p>
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                                Quantité réelle
                                            </label>
                                            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2">
                                                <input
                                                    type="number"
                                                    step="0.001"
                                                    value={realValue ?? ''}
                                                    onChange={(event) => updateDraftValue(item.id, 'quantity_real', event.target.value)}
                                                    required
                                                    className="w-full border-none bg-transparent text-sm text-slate-900 outline-none"
                                                />
                                                <span className="text-sm text-slate-500">{item.ingredient?.unit?.abbreviation || ''}</span>
                                            </div>
                                            {diff !== null && (
                                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${diffClass}`}>
                                                    {formatDifference(diff)}
                                                </span>
                                            )}
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                                Notes
                                            </label>
                                            <input
                                                type="text"
                                                value={draft.notes !== undefined ? draft.notes : item.notes || ''}
                                                onChange={(event) => updateDraftValue(item.id, 'notes', event.target.value)}
                                                placeholder="Optionnel"
                                                className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none"
                                            />
                                        </div>

                                        <div className="flex justify-end">
                                            <button
                                                type="submit"
                                                disabled={savingItemId === item.id}
                                                className="inline-flex items-center justify-center rounded-3xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                                            >
                                                {savingItemId === item.id ? 'Enregistrement…' : 'Enregistrer'}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            );
                        })}
                    </div>
                ) : (
                    <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-600 shadow-sm">
                        Aucun ingrédient à inventorier pour cette date.
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="h-screen overflow-y-auto px-6 py-6">
            <section className="mb-6 rounded-3xl border border-emerald-400 bg-emerald-50/90 p-6 shadow-xl shadow-slate-950/10">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-emerald-500">Inventaires</p>
                        <h2 className="text-3xl font-semibold text-emerald-900">Inventaire des ingrédients</h2>
                    </div>
                    <div className="space-y-2 text-sm text-emerald-700 sm:text-right">
                        <p>Créez un inventaire hebdomadaire pour comparer vos quantités réelles aux attentes.</p>
                        <p>Vous pouvez ensuite compléter chaque ligne depuis la page d’inventaire.</p>
                    </div>
                </div>
            </section>

            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-xl font-semibold text-slate-900">Inventaires récents</h3>
                    <p className="text-sm text-slate-600">Liste des sessions d’inventaire créées et lien vers les détails.</p>
                </div>
                <button
                    type="button"
                    onClick={() => setCreateFormOpen(true)}
                    disabled={creating}
                    className="inline-flex items-center justify-center rounded-3xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                >
                    {creating ? 'Création…' : 'Nouveau inventaire'}
                </button>
            </div>

            {createFormOpen && (
                <div className="mb-6 rounded-3xl border border-amber-300 bg-amber-50 p-6 shadow-sm">
                    <h3 className="mb-4 text-lg font-semibold text-amber-900">Créer un inventaire</h3>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                            <label className="block text-sm font-semibold text-amber-900">Service</label>
                            <select
                                value={createForm.service || ''}
                                onChange={(e) => setCreateForm((p) => ({ ...p, service: e.target.value || null }))}
                                className="mt-2 w-full rounded-xl border border-amber-200 px-3 py-2 text-sm outline-none"
                            >
                                <option value="">Tous les services</option>
                                <option value="cuisine">Cuisine</option>
                                <option value="barista">Barista</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-amber-900">Type de stock</label>
                            <select
                                value={createForm.inventory_type}
                                onChange={(e) => setCreateForm((p) => ({ ...p, inventory_type: e.target.value }))}
                                className="mt-2 w-full rounded-xl border border-amber-200 px-3 py-2 text-sm outline-none"
                            >
                                <option value="main">Stock principal</option>
                                <option value="work">Stock de travail</option>
                            </select>
                        </div>
                        <div className="flex items-end gap-2">
                            <button
                                type="button"
                                onClick={createInventory}
                                disabled={creating}
                                className="flex-1 inline-flex items-center justify-center rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-500 disabled:opacity-60"
                            >
                                {creating ? 'Création…' : 'Créer'}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setCreateFormOpen(false);
                                    setCreateForm({ service: null, inventory_type: 'main' });
                                }}
                                className="inline-flex items-center justify-center rounded-xl border border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100"
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {error && (
                <div className="mb-6 rounded-3xl border border-rose-300 bg-rose-50 p-4 text-rose-700">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-6 text-slate-500 shadow-sm">
                    Chargement des inventaires…
                </div>
            ) : inventories.length === 0 ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-3xl">📦</div>
                    <h3 className="text-xl font-semibold text-slate-900">Aucun inventaire trouvé</h3>
                    <p className="mt-2 text-sm text-slate-600">Commencez par créer votre premier inventaire hebdomadaire.</p>
                    <button
                        type="button"
                        onClick={createInventory}
                        disabled={creating}
                        className="mt-6 inline-flex items-center justify-center rounded-3xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                    >
                        {creating ? 'Création…' : 'Créer un inventaire'}
                    </button>
                </div>
            ) : (
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Date</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Service</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Type</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Créé par</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Lignes</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                            {inventories.map((inventoryItem) => (
                                <tr key={inventoryItem.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-4 text-sm text-slate-700">{formatDate(inventoryItem.inventory_date)}</td>
                                    <td className="px-4 py-4 text-sm text-slate-700">
                                        {inventoryItem.service ? (
                                            <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 capitalize">
                                                {inventoryItem.service}
                                            </span>
                                        ) : (
                                            <span className="text-slate-400">Tous</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-slate-700">
                                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${inventoryItem.inventory_type === 'work' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                                            {inventoryItem.inventory_type === 'work' ? 'Travail' : 'Principal'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-slate-700">{inventoryItem.creator?.name || '—'}</td>
                                    <td className="px-4 py-4 text-sm text-slate-700">{inventoryItem.items_count ?? '—'}</td>
                                    <td className="px-4 py-4 text-right text-sm">
                                        <a
                                            href={`/ingredient-inventories/${inventoryItem.id}`}
                                            className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-700 transition hover:bg-slate-100"
                                        >
                                            Voir
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
